import { computed, unref } from 'vue'
import { useHead } from '@unhead/vue'
import { useSeoStore } from '@/stores/seo'
import i18n from '@/locales'

/**
 * Resolve value yang bisa berupa:
 * - string biasa
 * - computed ref / ref
 * - undefined / null
 * Selalu return string (tidak pernah undefined).
 */
function resolveString(value, fallback = '') {
  const v = unref(value)  // resolve ref & computed
  if (v === undefined || v === null || v === '') {
    return fallback
  }
  return String(v)
}

export function useSeo(overrides = {}) {
  const seo = useSeoStore()

  const title = computed(() =>
    resolveString(
      unref(overrides.title),
      resolveString(seo.title, 'DITAP')
    )
  )

  const description = computed(() =>
    resolveString(
      unref(overrides.description),
      resolveString(seo.description, '')
    )
  )

  const keywords = computed(() =>
    resolveString(
      unref(overrides.keywords),
      resolveString(seo.keywords, '')
    )
  )

  const image = computed(() =>
    resolveString(
      unref(overrides.image),
      resolveString(seo.image, '')
    )
  )

  // Lebar & tinggi OG image — dipakai untuk og:image:width / og:image:height
  const imageWidth = computed(() => {
    const v = unref(overrides.imageWidth)
    const resolved = v ?? seo.imageWidth
    return Number(resolved) || 0
  })

  const imageHeight = computed(() => {
    const v = unref(overrides.imageHeight)
    const resolved = v ?? seo.imageHeight
    return Number(resolved) || 0
  })

  const favicon = computed(() =>
    resolveString(
      unref(overrides.favicon),
      resolveString(seo.favicon, '/favicon.ico')
    )
  )

  const url = computed(() =>
    resolveString(
      unref(overrides.url),
      typeof window !== 'undefined'
        ? window.location.href
        : ''
    )
  )

  const type = computed(() =>
    resolveString(unref(overrides.type), 'website')
  )

  const robots = computed(() =>
    resolveString(unref(overrides.robots), 'index, follow')
  )

  const siteName = computed(() =>
    resolveString(unref(overrides.siteName), 'DITAP')
  )

  const locale = computed(() =>
    resolveString(unref(overrides.locale), 'id_ID')
  )

  useHead({
    title,
    link: [
      { rel: 'icon', href: favicon },
      { rel: 'canonical', href: url }
    ],
    meta: [
      // Basic
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'robots', content: robots },

      // Open Graph
      { property: 'og:type', content: type },
      { property: 'og:site_name', content: siteName },
      { property: 'og:locale', content: locale },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: image },
      { property: 'og:url', content: url },

      // OG image size — hanya inject kalau ada dimensi
      ...(imageWidth.value > 0 && imageHeight.value > 0
        ? [
            {
              property: 'og:image:width',
              content: String(imageWidth.value)
            },
            {
              property: 'og:image:height',
              content: String(imageHeight.value)
            }
          ]
        : []),

      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image }
    ]
  })

  return { title, description, keywords, image }
}

/**
 * Shortcut untuk halaman yang title/description/keywords-nya
 * diambil dari i18n.
 *
 * Contoh:
 *   useSeoFromI18n('meta.home')
 *   → title = i18n.t('meta.home.title')
 *   → description = i18n.t('meta.home.description')
 *   → keywords = i18n.t('meta.home.keywords')
 *
 * Bisa tambah override lain via parameter kedua:
 *   useSeoFromI18n('meta.notFound', { robots: 'noindex, nofollow' })
 *   useSeoFromI18n('meta.login', { robots: 'noindex, nofollow' })
 */
export function useSeoFromI18n(prefix, extra = {}) {
  useSeo({
    title: computed(() => i18n.global.t(`${prefix}.title`)),
    description: computed(() => i18n.global.t(`${prefix}.description`)),
    keywords: computed(() => i18n.global.t(`${prefix}.keywords`)),
    ...extra
  })
}